<template>

    <div class="max-w-5xl min-h-full mx-auto space-y-3 sm:pt-16">
        <div class="flex min-w-[300px] items-start justify-center overflow-hidden border-y border-zinc-200 bg-white sm:mx-0 sm:max-w-full sm:rounded-lg sm:border dark:border-white/10 dark:bg-zinc-900 px-6 py-b sm:px-8">
            <div class="w-full">
                <section>
                  <div v-if="showConfirmationModal"
                       class="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50">
                    <div class="w-full max-w-3xl px-6 py-8 mx-4 bg-white border border-2 rounded-lg border-clay-500">
                      <h2 class="text-2xl font-bold text-center">Almost there! Just a quick review.</h2>
                      <p class="mb-4 text-xl text-center text-clay-800">
                        You’re about to submit your audition/job listing - exciting!</p>
                        <div class="max-w-xl p-4 mx-auto bg-clay-50 sm:px-8 sm:py-6 rounded-2xl">
                            <p class="mb-4 ">
                                Just a heads-up: the following details can’t be changed after submission, so take a quick look
                                to make sure everything’s just right.
                            </p>
                            <p class="mb-2">
                                <i class="fas fa-map-marker-alt"></i> <strong>Region: </strong>
                                <span>{{ selectedTerritory.name || 'No region selected' }}</span>
                            </p>
                            <p class="mb-2">
                                <i class="fas fa-calendar-alt"></i> <strong>Start Date: </strong>
                                <span>{{ opportunity.startDate }}</span>
                            </p>
                            <p class="mb-4">
                                <i class="fas fa-calendar-alt"></i> <strong>End Date: </strong>
                                <span>{{ opportunity.endDate }}</span>
                            </p>
                            <p class="mb-4">
                                <i class="fas fa-theater-masks"></i> <strong>Organization: </strong>
                                <span>{{ selectedCompany.name }}</span>
                            </p>
                        </div>
                      
                      <p class="pt-6 pb-6 text-lg text-center text-clay-800">
                        If everything looks good, go ahead and submit!<br>
                        If you need to tweak something, you can go back and edit.
                      </p>
                      <div class="flex justify-center mt-2 space-x-4">
                          <div v-if="isSubmitting"
                               class="w-full inline-flex items-center justify-center px-4 py-[calc(theme(spacing.2)-1px)] rounded-full border border-transparent bg-clay-950 text-clay-100 hover:bg-clay-900 hover:text-clay-50 shadow-md whitespace-nowrap text-base font-medium data-[disabled]:bg-clay-50 data-[hover]:bg-clay-900 data-[disabled]:text-clay-600">
                              {{ isProMember ? 'Publishing listing...' : 'Continuing to payment...'}}
                          </div>

                          <div v-else>
                              <button @click.prevent="showConfirmationModal = false"
                                 class="px-4 py-2 mx-2 bg-gray-200 rounded cursor-pointer hover:bg-gray-300">
                                  Go Back & Edit
                              </button>
                              <button @click.prevent="submit"
                                 class="inline-flex items-center px-5 py-2 mx-2 font-medium text-white border border-transparent rounded-md shadow-sm bg-electric-600 hover:bg-electric-500 focus:outline-hidden focus:ring-2 focus:ring-electric-500 focus:ring-offset-2">
                                  {{ isProMember ? 'Publish Listing' : 'Continue to Payment'}}
                              </button>
                          </div>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="isProMember && submitSuccessful"
                         class="min-w-full min-h-full px-4 py-16 mx-auto bg-white sm:px-6 sm:py-12 md:grid md:place-items-center lg:px-8 rounded-2xl">
                        <main class="mx-auto text-center">

                            <div v-if="postAnOpportunityResponse.isPublished"
                                 class="pro-opp-checkout">
                                <div class="space-y-3 ">
                                    <h1 class="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
                                        Thank you!</h1>
                                    <p class="my-4 text-xl leading-6 text-clay-800">
                                        Your opportunity has successfully been posted on
                                        StageAgent.</p>
                                    <p class="">Share this opportunity to your
                                        network </p>
                                    <SaSocialShare
                                            :title="postAnOpportunityResponse.title"
                                            :url="generateOpportunityUrl(postAnOpportunityResponse)">
                                    </SaSocialShare>

                                </div>
                                <p class="my-8 text-center">
                                    <svg id="fi_5238925"
                                         enable-background="new 0 0 480 480" height="90"
                                         viewBox="0 0 480 480" width="90"
                                         xmlns="http://www.w3.org/2000/svg"
                                         class="mx-auto">
                                        <path d="m448 386.001h-36v-47c0-13.25-10.75-24-24-24h-19v-42c0-13.25-10.75-24-24-24h-210c-13.25 0-24 10.75-24 24v42h-19c-13.25 0-24 10.75-24 24v47h-36c-13.25 0-24 10.75-24 24v28c0 13.25 10.75 24 24 24h416c13.25 0 24-10.75 24-24v-28c0-13.25-10.75-24-24-24z"
                                              fill="#cbe3fa"></path>
                                        <path d="m448.524 412.001h-379.524c-13.255 0-24 10.745-24 24v13c0 7.18-5.82 13-13 13-13.255 0-24-10.745-24-24v-28c0-13.255 10.745-24 24-24h416c18.574 0 17.381 26 .524 26z"
                                              fill="#eef7ff"></path>
                                        <path d="m388.352 338.001h-260.352c-13.255 0-24 10.745-24 24v16c0 4.418-3.582 8-8 8h-28v-47c0-13.255 10.745-24 24-24 0 0 296.805.01 297.203.03 14.759.729 13.927 22.97-.851 22.97z"
                                              fill="#eef7ff"></path>
                                        <path d="m345.487 269.001h-179.487c-13.255 0-24 10.745-24 24v12.001c0 5.522-4.477 9.999-9.999 9.999h-21.001v-42c0-13.255 10.745-24 24-24 0 0 210.846.011 211.265.033 12.805.666 12.044 19.967-.778 19.967z"
                                              fill="#eef7ff"></path>
                                        <path d="m240 171.571c-.1-.05 1.414-.784-30.6 16.11-14.499 7.651-31.45-4.71-28.68-20.93l5.83-34.15-24.72-24.19c-11.73-11.48-5.26-31.49 10.96-33.86l34.16-4.98 15.28-31.07c7.25-14.76 28.21-14.76 35.46 0l15.28 31.07 34.17 4.98c16.21 2.37 22.69 22.38 10.95 33.86l-24.72 24.19 5.84 34.15c2.77 16.22-14.18 28.58-28.69 20.93z"
                                              fill="#ffe773"></path>
                                        <path d="m469.434 186.651c5.11 7.31-.12 17.35-9.04 17.35h-13.38c-.328 17.418-.261 47.954.293 64.489.151 4.52-3.473 8.271-7.995 8.271h-20.637c-4.523 0-8.144-3.748-7.993-8.268.554-16.534.62-47.074.292-64.492h-13.37c-8.92 0-14.16-10.05-9.04-17.36l31.4-44.9c4.39-6.27 13.68-6.27 18.07 0z"
                                              fill="#ffe773"></path>
                                        <path d="m421.159 276.761h-2.484c-4.519 0-8.142-3.751-7.993-8.268.31-9.365.472-36.801.491-44.657.013-5.353 4.297-9.703 9.649-9.824 5.997-.135 10.244 4.459 10.301 9.724.092 8.387.055 35.456-.118 43.385-.117 5.355-4.49 9.64-9.846 9.64z"
                                              fill="#fff0a6"></path>
                                        <path d="m440.118 144.723c1.379 1.974 1.462 4.699-.154 7.018-33.677 48.33-31.671 45.216-32.159 46.142-1.96 3.723-5.866 6.118-10.201 6.118-8.92 0-14.16-10.05-9.04-17.36l31.4-44.9c4.39-6.27 13.68-6.27 18.07 0z"
                                              fill="#fff0a6"></path>
                                        <path d="m91.44 146.651c5.11 7.31-.12 17.35-9.04 17.35h-13.38c-.338 17.92-.257 60.194.342 84.573.11 4.493-3.503 8.188-7.997 8.188h-20.734c-4.494 0-8.102-3.696-7.992-8.189.599-24.379.679-66.651.341-84.571h-13.37c-8.92 0-14.16-10.05-9.04-17.36l31.4-44.9c4.39-6.27 13.68-6.27 18.07 0z"
                                              fill="#ffe773"></path>
                                        <g fill="#fff0a6">
                                            <path d="m62.124 104.723c1.474 2.11 1.347 4.873-.129 6.981-33.705 48.167-31.696 45.25-32.184 46.178-1.99 3.779-5.93 6.118-10.201 6.118-8.92 0-14.16-10.05-9.04-17.36l31.4-44.9c4.39-6.27 13.68-6.27 18.07 0z"></path>
                                            <path d="m42.996 256.761h-2.365c-4.506 0-8.102-3.684-7.992-8.189.428-17.534.593-44.328.52-64.66-.019-5.374 4.261-9.775 9.634-9.895 6.014-.134 10.268 4.487 10.313 9.773.146 17.14.076 43.119-.241 63.262-.086 5.39-4.479 9.709-9.869 9.709z"></path>
                                            <path d="m257.69 38.501c2.319 4.719.041 10.361-4.871 12.239-4.36 1.666-8.212 4.921-10.589 9.761l-13.216 26.872c-1.292 2.628-3.796 4.45-6.694 4.873l-29.53 4.304c-7.044 1.031-12.249 5.386-14.922 10.925-2.671 5.535-9.852 6.989-14.245 2.69l-1.794-1.755c-11.73-11.48-5.26-31.49 10.96-33.86l34.16-4.98 15.28-31.07c7.251-14.759 28.209-14.758 35.461.001z"></path>
                                            <path d="m205.518 160.657-1.764 10.335c-2.518 14.74-24.818 11.919-23.214-2.948.096-.892.05-.53 1.892-11.332 1.588-9.311 12.981-13.007 19.733-6.401 2.746 2.687 3.999 6.558 3.353 10.346z"></path>
                                            <path d="m304.799 93.293-6.91-1.006c-3.079-.448-5.74-2.385-7.113-5.177-3.354-6.821 2.314-14.608 9.835-13.511 0 0 7.323 1.08 7.711 1.159 11.811 2.424 8.408 20.272-3.523 18.535z"></path>
                                        </g>
                                        <g>
                                            <path d="m177.961 135.384-5.13 30.022c-3.889 22.756 19.957 40.084 40.308 29.345l30.558-16.126c3.908-2.063 5.404-6.901 3.342-10.809-2.063-3.909-6.902-5.401-10.809-3.342l-30.558 16.126c-8.637 4.56-18.729-2.786-17.069-12.499l5.836-34.156c.442-2.589-.414-5.229-2.291-7.065l-24.722-24.189c-7.029-6.877-3.15-18.809 6.518-20.22l34.164-4.983c2.608-.38 4.862-2.021 6.024-4.386l15.28-31.076c4.326-8.799 16.768-8.818 21.102 0l15.278 31.075c1.163 2.366 3.417 4.007 6.025 4.387l34.165 4.983c9.663 1.41 13.548 13.34 6.518 20.22l-24.722 24.189c-1.877 1.836-2.733 4.477-2.291 7.065l5.836 34.155c1.226 7.177-3.738 12.364-9.031 13.602-4.302 1.006-6.975 5.309-5.969 9.611 1.005 4.301 5.309 6.977 9.611 5.969 14.346-3.354 23.641-17.355 21.16-31.876l-5.13-30.022 21.725-21.258c16.464-16.108 7.356-44.169-15.398-47.487l-29.998-4.376-13.421-27.296c-10.179-20.707-39.642-20.701-49.818-.001l-13.422 27.297-29.997 4.376c-22.8 3.325-31.837 31.404-15.398 47.487z"></path>
                                            <path d="m157.876 30.013c2.878 3.333 7.931 3.721 11.283.824 3.344-2.888 3.713-7.939.824-11.283l-5.859-6.783c-2.889-3.346-7.94-3.714-11.283-.824-3.344 2.888-3.713 7.939-.824 11.283z"></path>
                                            <path d="m109.99 106.686c.564 0-.116.044 11.737-1.038 4.399-.401 7.641-4.294 7.239-8.694s-4.3-7.637-8.694-7.239l-11 1.004c-4.399.401-7.641 4.294-7.239 8.694.379 4.152 3.867 7.273 7.957 7.273z"></path>
                                            <path d="m358.272 105.648c11.889 1.085 11.219 1.038 11.737 1.038 4.09 0 7.578-3.121 7.957-7.273.401-4.4-2.84-8.293-7.239-8.694l-11-1.004c-4.404-.396-8.293 2.84-8.694 7.239-.401 4.4 2.84 8.293 7.239 8.694z"></path>
                                            <path d="m127.223 196.252c2.336 3.724 7.268 4.885 11.028 2.526l8-5.019c3.743-2.349 4.874-7.286 2.526-11.028-2.348-3.743-7.287-4.871-11.028-2.526l-8 5.019c-3.743 2.348-4.874 7.285-2.526 11.028z"></path>
                                            <path d="m333.674 193.759 8 5.019c3.729 2.337 8.67 1.232 11.028-2.526 2.348-3.742 1.217-8.68-2.526-11.028l-8-5.019c-3.744-2.347-8.681-1.218-11.028 2.526-2.348 3.742-1.217 8.68 2.526 11.028z"></path>
                                            <path d="m310.766 30.838c3.327 2.876 8.382 2.534 11.283-.824l5.859-6.783c2.889-3.344 2.52-8.396-.824-11.283-3.343-2.889-8.395-2.519-11.283.824l-5.859 6.783c-2.889 3.343-2.52 8.395.824 11.283z"></path>
                                            <path d="m448 378.001h-372v-39c0-8.822 7.178-16 16-16h296c8.822 0 16 7.178 16 16 0 4.418 3.582 8 8 8s8-3.582 8-8c0-17.645-14.355-32-32-32h-11v-34c0-17.645-14.355-32-32-32h-210c-16.023 0-29.665 11.968-31.73 27.838-.571 4.382 2.519 8.396 6.899 8.966 4.384.576 8.396-2.521 8.966-6.899 1.032-7.927 7.853-13.904 15.865-13.904h210c8.822 0 16 7.178 16 16v34h-269c-17.645 0-32 14.355-32 32v47c0 4.418 3.582 8 8 8h380c8.822 0 16 7.178 16 16v28c0 8.822-7.178 16-16 16h-416c-8.822 0-16-7.178-16-16v-28c0-6.24 3.668-11.954 9.345-14.557 4.017-1.842 5.779-6.59 3.938-10.606-1.842-4.016-6.59-5.778-10.606-3.938-11.346 5.199-18.677 16.622-18.677 29.1v28c0 17.645 14.355 32 32 32h416c17.645 0 32-14.355 32-32v-28c0-17.645-14.355-32-32-32z"></path>
                                            <path d="m19.614 172.001h5.475c.222 22.826.043 65.063-.689 84.459-.171 4.529 3.452 8.302 8 8.302 4.278 0 7.825-3.386 7.988-7.698.837-22.168.961-73.777.592-93.215-.083-4.357-3.64-7.848-7.999-7.848h-13.367c-2.46 0-3.907-2.75-2.488-4.777l31.397-44.896c1.208-1.727 3.75-1.723 4.956 0l31.403 44.904c1.403 2.007-.009 4.769-2.484 4.769h-13.376c-4.359 0-7.916 3.489-7.998 7.848-.37 19.415-.248 71.015.59 93.215.166 4.414 3.873 7.844 8.296 7.692 4.415-.166 7.859-3.881 7.692-8.296-.732-19.422-.911-61.648-.688-84.459h5.484c15.378 0 24.411-17.332 15.596-29.938l-31.403-44.905c-7.563-10.815-23.614-10.818-31.179 0l-31.397 44.895c-8.816 12.602.213 29.948 15.599 29.948z"></path>
                                            <path d="m397.602 212.001h5.493c.207 18.778.047 44.73-.697 64.459-.171 4.521 3.443 8.302 7.999 8.302 4.279 0 7.827-3.386 7.989-7.698.866-22.957.952-54.209.59-73.215-.082-4.358-3.64-7.848-7.998-7.848h-13.376c-2.45 0-3.903-2.74-2.484-4.769l31.403-44.904c1.203-1.72 3.747-1.73 4.956 0l31.397 44.896c1.421 2.031-.034 4.777-2.488 4.777h-13.366c-4.359 0-7.916 3.49-7.999 7.848-.361 19.027-.274 50.286.592 73.215.167 4.416 3.896 7.865 8.296 7.692 4.415-.167 7.859-3.881 7.692-8.296-.744-19.707-.905-45.665-.698-64.459h5.483c15.378 0 24.419-17.339 15.6-29.947l-31.397-44.896c-7.565-10.818-23.614-10.816-31.179 0l-31.403 44.904c-8.814 12.602.212 29.939 15.595 29.939z"></path>
                                        </g>
                                    </svg>
                                </p>
                                <p class="mt-8 mb-3"><a
                                        class="font-medium underline hover:text-black"
                                        href="/my-posts/opportunities/auditions-jobs">Click
                                    here</a> to
                                    edit your posts or</p>
                                <a class="inline-flex items-center px-5 py-3 font-medium text-white uppercase border border-transparent rounded-md shadow-sm t bg-electric-600 hover:bg-electric-500 focus:outline-hidden focus:ring-2 focus:ring-electric-500 focus:ring-offset-2"
                                   href="/opportunities/post">Post
                                    another opportunity</a>
                            </div>
                        </main>

                    </div>
                  <form v-else class="bg-white">
                        <div class="mb-8">
                           
                            <ErrorSummary title="Oops! Some fields might be missing or contain validation errors. Please review and provide the required information to submit your listing:"
                                          :response-status="responseStatus"/>
                        </div>

                        <div class="max-w-full mx-auto text-center ">

                            <div v-if="!isProMember"
                                 class="max-w-full mx-auto space-y-3 rounded-sm bg-electric-100 px-2.5 py-1.5 text-sm font-semibold text-electric-900 text-center  basic-member-post  basic-member-post">
                                <p><span class="font-semibold">Step 1:</span> Fill out
                                    the opportunity details below to create and preview
                                    your listing.</p>
                                <p><span class="font-semibold">Step 2:</span> Submit
                                    your billing information (credit card required)</p>
                                <p>The posting rate is $9.95. Listings will run until
                                    the audition or interview end date or 90 days
                                    (whichever is shorter). <br>Or <a
                                            class="font-semibold underline"
                                            href="/membership/upgrade">upgrade to a PRO
                                        subscription</a> to post auditions and jobs for
                                    free!</p>
                            </div>

                            <div v-else
                                 class="max-w-full mx-auto space-y-3 rounded-sm bg-powder-50 px-2.5 py-1.5 text-sm font-semibold text-powder-700 text-center  pro-member-post">
                                <p class="text-lg font-semibold">Congratulations on
                                    being a PRO member!</p>
                                <p>You can post your opportunity for free.</p>
                                <p class="text-xs font-semibold text-powder-700">*with
                                    active PRO subscription</p>

                            </div>
                        </div>
                         <h1 class="mt-6 mb-3 text-3xl font-bold tracking-tight text-center md:text-4xl font-display text-clay-950">
                                Post an Audition or Job Opportunity</h1>
                        <div class="mt-6">
                            <div class="text-red-600"> * <span class="text-clay-950"> 
                                Required field</span>
                            </div>
                            <h3 class="text-xl font-semibold">Basics</h3>
                            <div class="mt-3">
                                <label class="block font-bold tracking-tight font-display text-clay-950"
                                       for="title">Audition / Job Title<span
                                        class="text-red-600">*</span>
                                </label>
                                <input id="title" v-model="opportunity.title"
                                       class="block w-full px-3 py-2 mt-1 w-full py-2 bg-white border border-clay-500 rounded-md shadow-sm focus:border-clay-500 focus:outline-hidden focus:ring-1 focus:ring-clay-500 sm:text-sm"
                                       name="title"
                                       placeholder="e.g. Auditions for West Side Story"
                                       type="text">

                            </div>

                            <div class="mt-3">
                                <label class="block font-bold tracking-tight font-display text-clay-950"
                                       for="category">Category
                                    <span class="text-red-600">*</span>
                                </label>
                                <SaSelect
                                        id="category"
                                        v-model="opportunity.category"
                                        :options="categoryOptions"
                                        name="category"/>
                            </div>

                            <div class="mt-3">
                                <div id="opportunity-region-select" class="">
                                    <div class="">
                                        <h2 class="block font-bold tracking-tight font-display text-clay-950">
                                            Primary Region
                                            <span class="text-red-600">*</span></h2>

                                        <p class="pt-1 mt-1 text-sm text-gray-500">Select the
                                            nearest metro area to promote this
                                            opportunity.
                                            <a v-if="selectedTerritory.id"
                                               class="text-sa-red-600"
                                               @click="removeSelectedTerritory">
                                                <TrashIcon class="inline h-4 w-4>"/>
                                            </a>
                                        </p>
                                    </div>

                                    <TerritoriesTab :territories="territories"
                                                    v-model="selectedTerritory"
                                                    @onRegionSelected="onRegionSelectedFromTab"></TerritoriesTab>

                                </div>
                            </div>

                            <div class="mt-3">
                                <fieldset>
                                    <legend class="block font-bold tracking-tight font-display text-clay-950">
                                        Audition / Interview Format
                                        <span class="text-red-600">*</span>
                                    </legend>
                                    <div class="grid grid-cols-1 mt-4 space-x-6 gap-y-4 sm:grid-cols-3">
                                        <div v-for="format in formatOptions"
                                             :key="format.name"
                                             class="flex mb-4 opportunity-select">
                                            <label :for="format.name"
                                                   class="block min-w-full gap-4 p-4 font-medium text-gray-700 border border-clay-500 rounded-2xl bg-clay-50">
                                            <input :id="format.name"
                                                   v-model="opportunity.format"
                                                   :value="format.name"
                                                   class="w-4 h-4 mt-1 mr-2 border-gray-300 text-clay-600 focus:ring-clay-500"
                                                   name="format"
                                                   type="radio">
                                            
                                                {{ format.name }}
                                                <span class="block ml-6 text-sm font-light text-gray-600">
                                                    {{ format.description }}
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>

                            <div class="mt-3">
                                <div class="divide-y divide-gray-200">
                                    <div class="grid gap-6 mt-6 md:grid-cols-4">
                                        <div class="sm:col-span-4">
                                            <label class="block font-bold tracking-tight font-display text-clay-950">
                                                Cover Image <span
                                                    class="text-xs font-medium text-gray-500">(optional)</span>
                                            </label>
                                            <div class="flex justify-center px-6 pt-5 pb-6 mt-1 border-2 border-gray-300 border-dashed rounded-md">
                                                <div v-if="opportunity.image && opportunity.image.length > 0">
                                                    <div
                                                            class="overflow-hidden bg-gray-100 border rounded-lg shadow-lg aspect-square border-slate-900">
                                                        <img :src="opportunity.image"
                                                             class="object-cover object-center">
                                                    </div>
                                                    <button class="relative flex items-center justify-center px-8 py-2 text-sm font-medium text-gray-900 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200"
                                                            type="button"
                                                            @click.prevent="removeLogo">
                                                        <TrashIcon
                                                                class="float-right w-5 h-5 text-red-300 hover:text-red-500"/>
                                                    </button>
                                                </div>
                                                <div v-else
                                                     class="space-y-1 text-center">
                                                    <Uploadcare
                                                            :imagesOnly="true"
                                                            :validators="fileValidators"
                                                            crop="400x400 upscale"
                                                            publicKey="c1ecae68d0087aa3986a"
                                                            tabs="file url facebook gdrive dropbox instagram evernote flickr skydrive"
                                                            @error="onUploadImageFailed"
                                                            @success="(response: any) => onUploadSuccess(response)">
                                                        <svg aria-hidden="true"
                                                             class="w-12 h-12 mx-auto text-gray-400"
                                                             fill="none"
                                                             stroke="currentColor"
                                                             viewBox="0 0 48 48">
                                                            <path
                                                                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                                    stroke-linecap="round"
                                                                    stroke-linejoin="round"
                                                                    stroke-width="2"></path>
                                                        </svg>
                                                        <div class="flex text-sm text-gray-600">
                                                            <a
                                                                    class="relative flex items-center justify-center px-8 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-clay-800 hover:bg-clay-950">Upload
                                                                a File</a>
                                                        </div>
                                                        <p class="text-xs text-gray-500">
                                                            PNG, JPG, GIF up to
                                                            4MB
                                                        </p>
                                                    </Uploadcare>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            <div class="grid gap-4 mt-3 space-x-6 sm:grid-cols-2">
                                <div class="">
                                    <label class="block font-bold tracking-tight font-display text-clay-950"
                                           for="format">Audition / Interview Start Date
                                        <span class="text-red-600">*</span>
                                    </label>
                                    <cleave v-model="opportunity.startDate"
                                            :options="dateOptions"
                                            :raw="false"
                                            placeholder="MM/DD/YYYY"
                                            class="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                            name="startDate"
                                            type="text"/>
                                    
                                    <p class="pt-1 text-sm text-gray-500">E.g. the first day of the audition</p>

                                </div>

                                <div class="">
                                    <label class="block font-bold tracking-tight font-display text-clay-950"
                                           for="format">Audition / Interview End Date
                                        <span class="text-red-600">*</span>
                                    </label>
                                    <cleave v-model="opportunity.endDate"
                                            :options="dateOptions"
                                            :raw="false"
                                            placeholder="MM/DD/YYYY"
                                            class="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                            name="startDate"
                                            type="text"/>

                                    <p class="pt-1 text-sm text-gray-500">E.g. the last day of the audition or callbacks (your listing will be removed on this date)</p>
                                </div>
                            </div>
                        </div>

                        <div class="mt-6">
                            <hr class="border-gray-200">
                            <h3 class="text-xl font-semibold">Details</h3>
                            
                            <div class="mt-3">
                                <label class="block font-bold tracking-tight font-display text-clay-950"
                                       for="union">Union Requirements
                                    <span class="text-red-600">*</span>
                                </label>
                                <SaSelect id="union"
                                            v-model="opportunity.union"
                                            :options="unionOptions"
                                            name="union"/>
                            </div>

                            <div class="mt-3">
                                <fieldset id="opportunity-comp" class="">
                                    <legend class="block font-bold tracking-tight font-display text-clay-950">
                                        Compensation
                                        <span class="text-red-600">*</span>
                                    </legend>
                                    <div class="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-3">
                                        <div v-for="compensation in compensationOptions"
                                             :key="compensation.name"
                                             class="flex mb-4 opportunity-select ">
                                             <label :for="compensation.name"
                                                   class="block min-w-full gap-4 p-4 font-medium text-gray-700 border border-clay-500 rounded-2xl bg-clay-50">
                                                <input :id="compensation.name"
                                                    v-model="opportunity.compensation"
                                                    :value="compensation.name"
                                                    class="w-4 h-4 mt-1 mr-2 border-gray-300 text-clay-600 focus:ring-clay-500"
                                                    name="compensation"
                                                    type="radio">
                                                
                                                    {{ compensation.name }}
                                                <span class="block pl-6 text-sm font-light text-gray-600">
                                                    {{ compensation.description }}
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>

                            <div class="mt-3">
                                <label class="block font-bold tracking-tight font-display text-clay-950"
                                       for="description">Description <small>(Max. 10000
                                        characters)</small>
                                    <span class="text-red-600"> *</span>

                                </label>
                                <p class="text-sm text-gray-500">Describe your opportunity below. Include all relevant details such as directions, sign up forms, ticket prices, discount codes etc.</p>
                                <div class="mt-4">
                                    <textarea
                                            id="description"
                                            v-model="opportunity.description"
                                            class="h-32 resize p-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-clay-900 outline-1 -outline-offset-1 outline-clay-300 placeholder:text-clay-900 focus:outline-2 focus:-outline-offset-2 focus:outline-clay-600 sm:text-sm/6"
                                            name="description"
                                    />
                                </div>


                            </div>

                            <div class="mt-3">
                                <label class="block font-bold tracking-tight font-display text-clay-950"
                                       for="selectedShow">Related
                                    StageAgent Guide <span
                                            class="text-xs text-gray-500">(optional)</span></label>
                                <p class="text-sm text-gray-500">Include a link to a
                                    StageAgent guide</p>
                                <Combobox v-model="selectedShow" as="div" nullable>
                                    <div class="relative mt-1">
                                        <ComboboxInput
                                                :display-value="displaySelectedShow"
                                                class="w-full py-2 pl-3 pr-10 bg-white border border-gray-300 rounded-md shadow-sm focus:border-clay-500 focus:outline-hidden focus:ring-1 focus:ring-clay-500 sm:text-sm"
                                                placeholder="Start typing and select from the dropdown list"
                                                @change="getShows"/>
                                        <ComboboxButton
                                                class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-hidden" aria-label="Expand Button">
                                            <ChevronDownIcon aria-hidden="true"
                                                             class="w-5 h-5 text-gray-400"/>
                                        </ComboboxButton>
                                        <ComboboxOptions
                                                class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 focus:ring-1 focus:ring-clay-500 focus:ring-opacity-5 focus:outline-hidden sm:text-sm">

                                            <ComboboxOption v-for="show in shows"
                                                            :key="show.id"
                                                            v-slot="{ active, selected }"
                                                            :value="show"
                                                            as="template">
                                                <li
                                                        :class="['relative cursor-default select-none py-2 pl-8 pr-4', active ? 'bg-clay-600 text-white' : 'text-gray-900']">
                                                    <div class="flex items-center">
                                                        <img v-if="show.logo"
                                                             :src="show.logo"
                                                             class="w-5 h-5 mr-2 shrink-0">

                                                        <span :class="['block truncate', selected && 'font-semibold']">
                                                                                        {{
                                                                show.title
                                                            }} ({{ show.category }})
                                                                                    </span>
                                                        <span v-if="selected"
                                                              :class="['absolute inset-y-0 left-0 flex items-center pl-1.5', active ? 'text-white' : 'text-clay-600']">
                                                                                        <CheckIcon aria-hidden="true"
                                                                                                   class="w-5 h-5"/>
                                                                                    </span>
                                                    </div>

                                                </li>
                                            </ComboboxOption>
                                        </ComboboxOptions>
                                    </div>
                                </Combobox>
                                <p v-if="selectedShow?.id"
                                   class="mt-2 text-sm font-medium text-gray-500">
                                    <img v-if="selectedShow.logo"
                                         :src="selectedShow.logo"
                                         class="inline-block w-5 h-5 mr-2">

                                    <a :href="`/${selectedShow.urlSlug}`"
                                       target="_blank"
                                       class="font-normal underline text-clay-600 hover:text-clay-700">
                                        {{ selectedShow.title }}
                                    </a>
                                </p>
                            </div>

                        </div>

                        <div class="mt-6">
                            <hr class="border-gray-200">
                            <h3 class="text-xl font-semibold">Contact</h3>

                            <div class="mt-3">
                                <label class="block font-bold tracking-tight font-display text-clay-950"
                                       for="companyName">Organization / Company Name
                                    <span class="text-red-600">*</span>
                                </label>

                                <Combobox v-model="selectedCompany" as="div" nullable>
                                    <div class="relative mt-1">
                                        <ComboboxInput
                                                :display-value="displaySelectedCompany"
                                                class="w-full py-2 pl-3 pr-10 bg-white border border-gray-300 rounded-md shadow-sm focus:border-clay-500 focus:outline-hidden focus:ring-1 focus:ring-clay-500 sm:text-sm"
                                                placeholder="Type or search to add"
                                                @change="getCompanies"/>
                                        <ComboboxButton
                                                class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-hidden" aria-label="Expand Button">
                                            <ChevronDownIcon aria-hidden="true"
                                                             class="w-5 h-5 text-gray-400"/>
                                        </ComboboxButton>
                                        <ComboboxOptions
                                                class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 focus:ring-1 focus:ring-clay-500 focus:ring-opacity-5 focus:outline-hidden sm:text-sm">

                                            <ComboboxOption v-if="queryCompany"
                                                            v-slot="{ active, selected }"
                                                            :value="queryCompany"
                                                            as="template">
                                                <li
                                                        :class="['relative cursor-default select-none py-2 pl-8 pr-4', active ? 'bg-clay-600 text-white' : 'text-gray-900']">
                                                                                    <span :class="['block truncate', selected && 'font-semibold']">
                                                                                        Add "{{ companyQuery }}"
                                                                                    </span>
                                                    <span v-if="selected"
                                                          :class="['absolute inset-y-0 left-0 flex items-center pl-1.5', active ? 'text-white' : 'text-clay-600']">
                                                                                        <CheckIcon aria-hidden="true"
                                                                                                   class="w-5 h-5"/>
                                                                                    </span>
                                                </li>
                                            </ComboboxOption>
                                            <ComboboxOption v-for="company in companies"
                                                            :key="company.id"
                                                            v-slot="{ active, selected }"
                                                            :value="company"
                                                            as="template">
                                                <li
                                                        :class="['relative cursor-default select-none py-2 pl-8 pr-4', active ? 'bg-clay-600 text-white' : 'text-gray-900']">
                                                    <div class="flex items-center">
                                                        <img v-if="company.logoUrl"
                                                             :src="company.logoUrl"
                                                             class="w-5 h-5 mr-2 shrink-0">

                                                        <span :class="['block truncate', selected && 'font-semibold']">
                                                                                            {{
                                                                company.name
                                                            }} ({{
                                                                company.shortAddressPrettified
                                                            }})
                                                                                        </span>
                                                        <span v-if="selected"
                                                              :class="['absolute inset-y-0 left-0 flex items-center pl-1.5', active ? 'text-white' : 'text-clay-600']">
                                                                                            <CheckIcon
                                                                                                    aria-hidden="true"
                                                                                                    class="w-5 h-5"/>
                                                                                        </span>
                                                    </div>
                                                </li>
                                            </ComboboxOption>
                                        </ComboboxOptions>
                                    </div>
                                </Combobox>
                                <p v-if="selectedCompany?.id" 
                                   class="mt-2 text-sm font-medium text-gray-500">
                                    <img v-if="selectedCompany.logoUrl"
                                         :src="selectedCompany.logoUrl"
                                         class="inline-block w-5 h-5 mr-2">
                                    
                                    <a :href="`/theatre-companies/${selectedCompany.intId}/${selectedCompany.slug}`"
                                        target="_blank"
                                        class="font-normal underline text-clay-600 hover:text-clay-700">
                                        {{ selectedCompany.name }}
                                    </a>
                                </p>
                                <p v-else class="pt-1 text-sm font-medium text-gray-500">Don't see
                                    your organization? <a
                                            href="/theatre-companies/submit"
                                            target="_blank"
                                            class="font-normal underline text-clay-600 hover:text-clay-700">Add
                                        it to our Theatre Company Directory</a></p>
                            </div>
                            <div class="mt-3">
                                <label class="block font-bold tracking-tight font-display text-clay-950"
                                       for="address">Audition/Interview Location <span
                                    class="text-xs text-gray-500">(optional)</span>
                                </label>

                                <VueGoogleAutocomplete
                                        id="address"
                                        name="address"
                                        class="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                        placeholder="Start typing"
                                        types="address"
                                        @placechanged="onPlaceChanged"
                                />
                                <label class="label">
                                    <span class="pt-1 text-sm text-gray-500">e.g. 1234 Broadway, New York, NY 10001</span>
                                </label>
                            </div>
                            <div class="grid grid-cols-1 gap-4 mt-4 space-x-3 sm:grid-cols-3">
                                <div class="mt-3">
                                    <label class="block font-bold tracking-tight font-display text-clay-950"
                                        for="webUrl">Website
                                        URL <span class="text-red-600">*</span></label>
                                    <input id="webUrl" v-model="opportunity.webUrl"
                                        class="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                        name="webUrl"
                                        placeholder="e.g. https://stageagent.com"
                                        type="text">
                                </div>
                                
                                <div class="mt-3">
                                    <label class="block font-bold tracking-tight font-display text-clay-950"
                                        for="email">Email

                                    </label>
                                    <input id="email" v-model="opportunity.email"
                                        class="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                        name="email"
                                        placeholder="e.g. info@stageagent.com"
                                        type="email">
                                </div>


                                <div class="mt-3">
                                    <label class="block font-bold tracking-tight font-display text-clay-950"
                                        for="phone">Phone</label>
                                    <input id="phone" v-model="opportunity.phone"
                                        class="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                        name="phone"
                                        placeholder="e.g. 2145541234"
                                        type="text">
                                </div>
                            </div>
                            

                        </div>
                        <div class="py-3 mt-10 text-center">
                            <SaButton @click.prevent="preview" :loading="isSubmitting" variant="primary">
                                <template #loading>
                                    <span class="font-semibold">Generating preview...</span>
                                </template>
                                <span class="font-semibold">Preview</span>
                            </SaButton>
                        </div>

                    </form>
                </section>

            </div>
        </div>
    </div>

</template>

<script lang="ts" setup>
import {computed, defineProps, onMounted, Ref, ref} from "vue";
import {client, fileValidators} from '@/shared';
import {
    GetAllTerritories,
    PostAnOpportunity,
    PostAnOpportunityResponse,
    ResponseStatus,
    SearchGuidesByCategory,
    SearchGuidesByCategoryResponse,
    ShowDto,
    Region,
    TheatreCompanyDTO
} from "@/dtos";
// @ts-ignore
import Uploadcare from 'uploadcare-vue/src/Uploadcare.vue';
import {CheckIcon, ChevronDownIcon, TrashIcon} from '@heroicons/vue/20/solid';
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions} from '@headlessui/vue';
import TerritoriesTab from "../Territories/TerritoriesTab.vue";
import VueGoogleAutocomplete from 'vue-google-autocomplete/src/VueGoogleAutocomplete.vue'
// @ts-ignore
import Cleave from 'vue-cleave-component';
import 'md-editor-v3/lib/style.css';
import dayjs from 'dayjs';
import {store} from "@/store";
import {trackEvent} from "@/shared";
import SaSocialShare from "@/main/components/SaSocialShare.vue";
import SaSelect from "@/main/components/SaSelect.vue";
import SaButton from "@/main/components/SaButton.vue";

const props = defineProps<{
    categoryOptions: [];
    compensationOptions: { name: string; description: string }[];
    formatOptions: { name: string; description: string }[];
    unionOptions: string[];
}>();


const isProMember = computed(() => {
    return store.saUserSession.subscriptionType === 'pro';
});

const categoryOptions = computed(() => {
    return props.categoryOptions;
});

const formatOptions = computed(() => {
    return props.formatOptions;
});

const unionOptions = computed(() => {
    return props.unionOptions;
});

const today = dayjs().format('YYYY-MM-DD');
const futureDate = dayjs().add(12, 'month').format('YYYY-MM-DD');

const dateOptions = ref({
    delimiter: '/',
    date: true,
    dateMin: today,
    dateMax: futureDate,
    datePattern: ['m', 'd', 'Y']
});

let shows: Ref<ShowDto[]> = ref([]);
const selectedShow = ref({} as ShowDto);
const query = ref('');

const getShows = async (event: any) => {
    query.value = event.target.value;
    if (query.value !== '' && query.value.length >= 3) {
        await client.get<SearchGuidesByCategoryResponse>(new SearchGuidesByCategory({
                q: query.value,
                category: 'Shows'
            }
        ))
            .then(res => {
                shows.value = res.results as ShowDto[];
            });
    }
};

const displaySelectedShow = (selectedShow: ShowDto) => {
    let display = '';

    if (selectedShow?.title) {
        display = `${selectedShow.title} (${selectedShow.category})`;
    }
    return display;
};

let companies: Ref<TheatreCompanyDTO[]> = ref([]);
const selectedCompany = ref({} as TheatreCompanyDTO);
const companyQuery = ref('');

const queryCompany = computed(() => {
    return companyQuery.value === '' || companyQuery.value.length < 3 ? null : {id: null, name: companyQuery.value}
});

const getCompanies = async (event: any) => {
    companyQuery.value = event.target.value;
    if (companyQuery.value !== '' && companyQuery.value.length >= 3) {
        await client.get<SearchGuidesByCategoryResponse>(new SearchGuidesByCategory({
            q: companyQuery.value,
            category: 'Companies'
        }))
            .then(res => {
                companies.value = res.results as TheatreCompanyDTO[];
            });
    }
};

const displaySelectedCompany = (selectedCompany: TheatreCompanyDTO) => {
    let display = '';

    if (selectedCompany?.name) {
        if (selectedCompany.shortAddressPrettified) {
            display = `${selectedCompany.name} (${selectedCompany.shortAddressPrettified})`;
        } else {
            display = `${selectedCompany.name}`;
        }
    }
    return display;
};

const territories: Ref<Array<Region>> = ref([]);
const selectedTerritory = ref({} as Region);
const territoryQuery = ref('');

let filteredTerritories = computed(() =>
    territoryQuery.value === ''
        ? territories.value
        : territories.value.filter((territory: Region) => {
                return territory.name.toLowerCase().includes(territoryQuery.value.toLowerCase())
            }
        )
)

const removeSelectedTerritory = async () => {
    selectedTerritory.value = {} as Region;
};

const onRegionSelectedFromTab = async (territory: Region) => {
    selectedTerritory.value = territory;
};

onMounted(async () => {
    await client.get<Array<Region>>(new GetAllTerritories())
        .then(res => {
            territories.value = res;
        });

    const previewWrapper = document.getElementById('md-editor-v3-preview-wrapper');
    if (previewWrapper) {
        previewWrapper.classList.add('hidden', 'sm:block');
    }
});

const generateOpportunityUrl = (opportunity: any) => {
    return `${window.location.origin}/${opportunity.id}`
}

let opportunity: Ref<PostAnOpportunity> = ref(new PostAnOpportunity({
    regionId: '',
    startDate: '',
    endDate: ''
}));

const isSubmitting = ref(false);
const showConfirmationModal = ref(false);
const submitSuccessful = ref(false);
const postAnOpportunityResponse = ref(new PostAnOpportunityResponse({ isPublished: false }));
let responseStatus: Ref<ResponseStatus | null> = ref(null);

const preview = async () => {
    isSubmitting.value = true;

    let request = { ...opportunity.value, isPreview: true };
    if (selectedShow.value && selectedShow.value.title) {
        request.showId = selectedShow.value.id;
    }
    if (selectedCompany.value && selectedCompany.value.name) {
        request.companyName = selectedCompany.value.name;
        request.companyId = selectedCompany.value.id;
    } else {
        request.companyName = '';
    }
    request.regionId = selectedTerritory.value?.id ?? '';

    await client.post<PostAnOpportunityResponse>(new PostAnOpportunity(request))
        .then(_ => {
            showConfirmationModal.value = true;
            responseStatus.value = null;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
        });
    window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
    isSubmitting.value = false;
};

const submit = async () => {
    isSubmitting.value = true;
    submitSuccessful.value = false;

    let request = { ...opportunity.value };
    if (selectedShow.value && selectedShow.value.title) {
        request.showId = selectedShow.value.id;
    }
    if (selectedCompany.value && selectedCompany.value.name) {
        request.companyName = selectedCompany.value.name;
        request.companyId = selectedCompany.value.id;
    } else {
        request.companyName = '';
    }
    request.regionId = selectedTerritory.value?.id ?? '';

    await client.post<PostAnOpportunityResponse>(new PostAnOpportunity(request))
        .then(response => {
            postAnOpportunityResponse.value = response;
            responseStatus.value = null;
            
            let status = postAnOpportunityResponse.value.isPublished ? 'ready_to_publish' : 'waiting_for_payment';

            trackEvent('opportunity_created', {
                content_id: response.id,
                content_title: opportunity.value.title,
                item_category: opportunity.value.category,
                opportunity_status: status,
                member_status: store.saUserSession.subscriptionType
            }, true, false);

            if (postAnOpportunityResponse.value.isPublished) {
                submitSuccessful.value = true;
                showConfirmationModal.value = false;

                trackEvent('opportunity_published', {
                    content_id: response.id,
                    content_title: opportunity.value.title,
                    item_category: opportunity.value.category,
                    member_status: store.saUserSession.subscriptionType
                }, true, false);
            }
            else{
                window.location.href = `/${response.id}/checkout`;
            }
            
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
        });
    window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
    isSubmitting.value = false;
};

const onPlaceChanged = async (_: any, placeResultData: any) => {
    opportunity.value.address = placeResultData.formatted_address;
};

const removeLogo = async () => {
    opportunity.value.image = "";
}

const onUploadSuccess = async (response: any) => {
    console.log(response);
    opportunity.value.image = response.cdnUrl;
}

const onUploadImageFailed = async (response: any) => {
    console.log({response});
}
</script>
<style>
</style>